import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Button, Dialog, H1, HStack, Icon, ShimmerText, Text, VStack } from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { HtmlContent } from '~/b4b/components/html-content/HtmlContent';
import { RoundedBorderBox } from '~/b4b/features/plan-benchmark/components/RoundedBorderBox';

import AiOrb from './assets/icon_ai_orb.svg';
import {
  GeneratePlanBenchmarkDocument,
  type GeneratePlanBenchmarkMutation,
} from './graphql/mutation.generatePlanBenchmark.generated';
import { PlanBenchmarkButton } from './PlanBenchmarkButton';

type Props = { planId: string };
type BenchmarkResult = NonNullable<GeneratePlanBenchmarkMutation['generatePlanBenchmark']['result']>;

const BenchmarkSkeleton = () => (
  <VStack css={{ pt: 'space.300', gap: 'space.150', width: 'size.full' }}>
    <ShimmerText textStyle="text50" css={{ width: '65%' }} />
    <ShimmerText textStyle="text25" css={{ width: '100%' }} />
    <ShimmerText textStyle="text25" css={{ width: '90%' }} />
    <ShimmerText textStyle="text25" css={{ width: '55%' }} />
  </VStack>
);

const AISummary = ({ title, body }: BenchmarkResult['aiSummary']) => (
  <RoundedBorderBox borderStyle="gradientRotateOnce" width="size.full">
    <VStack css={{ padding: 'space.200', gap: 'space.200', width: '100%' }}>
      <HStack css={{ gap: 'space.50' }}>
        <img src={AiOrb} alt="AI Icon" />
        <H1 variant="title100">{title}</H1>
      </HStack>

      <Box css={{ fontSize: 'fontSize.100' }}>
        <HtmlContent documentFragment={body} />
      </Box>

      <Box css={{ color: 'text.weak', fontSize: 'fontSize.25' }}>
        AI summary; accuracy is not guaranteed. See important disclosures and our privacy policy.
      </Box>
    </VStack>
  </RoundedBorderBox>
);

const BenchmarkContent = ({ aiSummary }: BenchmarkResult) => (
  <VStack css={{ gap: 'space.300', width: '100%' }}>
    <Text variant="text100" css={{ pt: 'space.150' }}>
      Here's your plan benchmark.
    </Text>
    <AISummary {...aiSummary} />
    <Button type="submit" css={{ width: 'fit-content' }}>
      View recommendations
    </Button>
  </VStack>
);

const BenchmarkError = ({ onRetry }: { onRetry: () => void }) => (
  <VStack
    css={{
      gap: 'space.200',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'space.400',
      maxWidth: '400px',
      margin: '0 auto',
    }}
  >
    <Icon name="ExclamationCircle" size="medium" color="icon" />
    <Text variant="text100" color="text" css={{ textAlign: 'center' }}>
      We were unable to generate your plan benchmark. Please try again.
    </Text>
    <Button
      variant="solidPrimary"
      onClick={onRetry}
      data-track-event="ElementClicked"
      data-track-name="PlanBenchmarkRetryButton"
      data-track-module="PlanBenchmark"
    >
      Try again
    </Button>
  </VStack>
);

export const PlanBenchmarkWidget: React.FC<Props> = ({ planId }) => {
  const [generate, { data, loading, error }] = useGraphQLMutation(GeneratePlanBenchmarkDocument);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (newOpen && !data && !loading) {
      void generate({ variables: { input: { planId } } });
    }
  };

  const result = data?.generatePlanBenchmark.result;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild={true}>
        <PlanBenchmarkButton />
      </Dialog.Trigger>
      <Dialog.Content
        size="large"
        aria-describedby={undefined}
        data-track-event="ElementViewed"
        data-track-name="PlanBenchmarkDialog"
        data-track-module="PlanBenchmark"
        css={{ p: 'space.400', minWidth: '400px', maxWidth: '800px' }}
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Plan benchmark</H1>
        </Dialog.Title>
        <ErrorBoundary name="plan-benchmark">
          {loading && <BenchmarkSkeleton />}
          {error && !loading && <BenchmarkError onRetry={() => void generate({ variables: { input: { planId } } })} />}
          {result && <BenchmarkContent {...result} />}
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
